import axios from "axios";
import Bluebird from "bluebird";

import { auth } from "@/auth";
import store from "@/store/root";
import { Document, Page, Session } from "@/tools/msa/models";
import { ConceptMatch } from "@/tools/msa/models/concepts";
import {
  Response,
  ConceptMatch as RawConceptMatch
} from "@/tools/msa/models/requests/analysis";
import { format, parse } from "date-fns/esm";
import { getNames } from "@/tools/msa/utils/names";
import { ConceptMatchItem } from "@/tools/msa/models/item";
import { PROVIDER_NAME } from "@/tools/msa/utils/names/provider";

const convertRawMatch = (page: Page, raw: RawConceptMatch): ConceptMatch => ({
  concept: {
    id: raw.concept.conceptid,
    name: raw.concept.conceptname,
    type: raw.concept.concepttype
  },
  context: raw.context,
  labels: raw.labels,
  refers: raw.refers
    ? Object.fromEntries(
        Object.entries(raw.refers).map(([key, match]) => [
          key,
          convertRawMatch(page, match)
        ])
      )
    : undefined,
  relationships: raw.relationships
    ? Object.fromEntries(
        Object.entries(raw.relationships).map(([key, value]) => [
          key,
          value.map(match => convertRawMatch(page, match))
        ])
      )
    : undefined,
  score: raw.score,
  text: raw.concept.text.phrase.trim(),
  locations: [page.tokens.slice(...raw.concept.text.tokpos)]
});

export const analyzeDocument = async (
  document: Document,
  session: Session
): Promise<Document> => {
  await axios
    .post<Response>(
      (store.state as any).settings.current.endpoints.analysis,
      document.tokens.map(token => `${token.text}${token.delimiter}`).join(""),
      {
        headers: { ...(await auth.headers()), "X-Session-Id": session.id }
      }
    )
    .then(response => response.data)
    .then(data => {
      document.date =
        document.date ||
        (/\d{1,4}-\d{1,2}-\d{1,2}/.test(data.meta.documentdate)
          ? format(parse(data.meta.documentdate, "y-M-d", new Date()), "M/d/y")
          : null);
      document.dateOfBirth =
        document.dateOfBirth ||
        (data.meta.patient &&
        data.meta.patient.dob &&
        /\d{1,4}-\d{1,2}-\d{1,2}/.test(data.meta.patient.dob)
          ? parse(data.meta.patient.dob, "y-M-d", new Date())
          : null);
      session.patient.dateOfBirth =
        session.patient.dateOfBirth || document.dateOfBirth;
    });

  await Bluebird.map(
    document.pages,
    async page =>
      await axios
        .post<Response>(
          (store.state as any).settings.current.endpoints.analysis,
          page.tokens.map(token => `${token.text}${token.delimiter}`).join(""),
          {
            headers: { ...(await auth.headers()), "X-Session-Id": session.id }
          }
        )
        .then(response => response.data)
        .then(data => {
          page.date = /\d{1,4}-\d{1,2}-\d{1,2}/.test(data.meta.documentdate)
            ? format(
                parse(data.meta.documentdate, "y-M-d", new Date()),
                "M/d/y"
              )
            : null;

          Object.values(data.content)
            .flatMap(matches =>
              matches.map(raw => Object.freeze(convertRawMatch(page, raw)))
            )
            .forEach(match =>
              document.items.push(
                Object.freeze({
                  type: "concept_match",
                  document,
                  match
                })
              )
            );
        })
  );

  document.providerName =
    document.providerName ||
    getNames(document, "name.drname", PROVIDER_NAME)[0] ||
    "";

  document.chiefComplaint =
    document.chiefComplaint ||
    [
      ...new Set(
        document.items
          .filter(item => item.type === "concept_match")
          .map(item => item as ConceptMatchItem)
          .filter(
            item =>
              item.match.labels && item.match.labels.includes("chief_complaint")
          )
          .map(item => item.match.text)
      )
    ].join("\n");

  return document;
};
